import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import TagSelector, { TagOption } from "@/components/TagSelector";

interface Permission {
	id: number;
	name: string;
}

type FormChangeEvent = {
	name: string;
	value: string | number[];
};

export type AddRoleFormProps = {
	formData: {
		name: string;
		permissions: number[];
	};
	isEditing: boolean;
	isSubmitting?: boolean;
	handleChange: (e: FormChangeEvent) => void;
	handleSubmit: (e: React.FormEvent) => void;
};

const AddRoleForm: React.FC<AddRoleFormProps> = ({
	formData,
	isEditing,
	isSubmitting = false,
	handleChange,
	handleSubmit
}) => {
	const [errors, setErrors] = useState({ name: "", permissions: "" });

	const { data: permissions = [] } = useQuery<Permission[]>({
		queryKey: ["system-permissions"],
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["PERMISSIONS", "LIST"]),
				{
					params: {
						scope: "SYSTEM"
					}
				}
			);
			return data;
		}
	});

	const validateAndSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!formData.name.trim()) {
			setErrors({ name: "Name is required", permissions: "" });
			return;
		}
		setErrors({ name: "", permissions: "" });
		handleSubmit(e);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		handleChange({
			name: e.target.name,
			value: e.target.value
		});
	};

	const handlePermissionChange = (selectedOptions: TagOption[]) => {
		handleChange({
			name: "permissions",
			value: selectedOptions.map((option) => Number(option.value))
		});
	};

	const permissionOptions: TagOption[] = permissions.map((permission) => ({
		value: permission.id.toString(),
		label: permission.name
	}));

	return (
		<div className="h-full">
			<form
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
				onSubmit={validateAndSubmit}
			>
				<div className="mb-4 flex-1">
					<div className="mb-4">
						<label
							htmlFor="name"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Role Name
						</label>
						<input
							name="name"
							type="text"
							placeholder="Enter role name"
							value={formData.name}
							onChange={handleInputChange}
							className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						/>
						{errors.name && (
							<p className="mt-1 text-sm text-red-600">{errors.name}</p>
						)}
					</div>
					<div className="mb-4">
						<label
							htmlFor="permissions"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Permissions
						</label>
						<TagSelector
							value={formData.permissions.map((id) => ({
								value: id.toString(),
								label: permissions.find((p) => p.id === id)?.name || ""
							}))}
							onChange={handlePermissionChange}
							options={permissionOptions}
							placeholder="Select permissions"
							className="focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md shadow-sm w-full"
						/>
						{errors.permissions && (
							<p className="mt-1 text-sm text-red-600">{errors.permissions}</p>
						)}
					</div>
				</div>
				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{isEditing ? "Updating..." : "Creating..."}
							</>
						) : isEditing ? (
							"Update Role"
						) : (
							"Create Role"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddRoleForm;
